/**
 * 🟥 MINI-SPRINT SSE — Hook React para suscribirse a /api/notifications/stream.
 *
 * Uso:
 *   const { connected, lastEvent } = useNotificationStream({
 *     enabled: !!user,           // gatekeeper: solo si hay sesión
 *     onNotification: (ev) => {
 *       // ev = { kind: 'notification', id, type, title_es, ..., metadata }
 *       toast(ev.title_es);
 *       refetchUnread();
 *     },
 *     onAdminEvent: (ev) => {
 *       // ev = { kind: 'submission_new', submission_id, type, ... }
 *       refetchModerationQueue();
 *     },
 *   });
 *
 * Reconexión automática: `EventSource` reintenta solo cada ~3s si el
 * server cierra. Si la app pierde foco, mantenemos la conexión abierta
 * (no la cerramos en visibilitychange — es eficiente).
 *
 * Limpieza: el effect cierra el EventSource al unmount.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { getBackendUrl } from '../config/api';

const API = getBackendUrl();
const STREAM_URL = `${API}/api/notifications/stream`;

export default function useNotificationStream({
  enabled = true,
  onNotification = null,
  onAdminEvent = null,
  onConnected = null,
} = {}) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const esRef = useRef(null);

  // Refs estables para callbacks — evita reconectar cuando cambian
  // identidades de funciones inline pasadas por el componente padre.
  const onNotifRef = useRef(onNotification);
  const onAdminRef = useRef(onAdminEvent);
  const onConnRef = useRef(onConnected);
  useEffect(() => { onNotifRef.current = onNotification; }, [onNotification]);
  useEffect(() => { onAdminRef.current = onAdminEvent; }, [onAdminEvent]);
  useEffect(() => { onConnRef.current = onConnected; }, [onConnected]);

  const close = useCallback(() => {
    if (esRef.current) {
      try { esRef.current.close(); } catch (_) { /* noop */ }
      esRef.current = null;
    }
    setConnected(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      close();
      return undefined;
    }
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return undefined;
    }
    // Usamos `withCredentials: true` para que las cookies de sesión
    // viajen junto al request del EventSource.
    let es;
    try {
      es = new EventSource(STREAM_URL, { withCredentials: true });
    } catch (e) {
      // Algunos browsers viejos pueden no soportar EventSource — fail-open.
      return undefined;
    }
    esRef.current = es;

    es.onopen = () => {
      // El server emite un evento `connected` después del open — confiamos en él.
    };

    es.onerror = () => {
      // EventSource reconecta solo. Solo actualizamos el flag UI.
      setConnected(false);
    };

    es.onmessage = (msgEvent) => {
      let payload;
      try {
        payload = JSON.parse(msgEvent.data);
      } catch (_) {
        return;
      }
      setLastEvent(payload);
      const kind = payload?.kind;
      if (kind === 'connected') {
        setConnected(true);
        if (typeof onConnRef.current === 'function') {
          try { onConnRef.current(payload); } catch (_) { /* noop */ }
        }
        return;
      }
      if (kind === 'notification') {
        if (typeof onNotifRef.current === 'function') {
          try { onNotifRef.current(payload); } catch (_) { /* noop */ }
        }
        return;
      }
      // Admin events: submission_new, submission_rejected_admin, etc.
      if (typeof onAdminRef.current === 'function') {
        try { onAdminRef.current(payload); } catch (_) { /* noop */ }
      }
    };

    return close;
  }, [enabled, close]);

  return { connected, lastEvent, close };
}
